import { useEffect, useState } from "react";

import { Box } from "@jti/core-components/components/Box";
import { type BundleConfigOption, type StockStatusElements } from "@jti/fe-common";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";

import { isInStock } from "@product-details/utils/stockStatus";

import { getInitialSticks } from "../../utils";

import ProductBundlesSingleStick from "./ProductBundlesSingleStick";

import "./ProductBundles.scss";

type Sticks = Record<string, number>;

const getCount = (sticks: Sticks): number =>
  Object.values(sticks).reduce((prev: number, current: number): number => prev + current, 0);

export type ProductBundlesStickBlockType = {
  title: string;
  listAriaLabel: string;
  options: BundleConfigOption[];
  stockStatusElements: StockStatusElements;
  onChange: (data: { quantities: { selection_id: string; qty: number }[]; value: string[] }) => void;
  showValidation: boolean;
  isLoading: boolean;
  sticksCount: number;
};

const ProductBundlesStickBlock = ({
  title,
  listAriaLabel,
  options,
  stockStatusElements,
  onChange,
  showValidation,
  isLoading,
  sticksCount,
}: ProductBundlesStickBlockType) => {
  const [currentSticks, setCurrentSticks] = useState<Record<string, number>>({});
  const [count, setCount] = useState(0);

  useEffect(() => {
    const initialSticks = getInitialSticks(options, sticksCount, stockStatusElements);
    setCurrentSticks(initialSticks);
    setCount(getCount(initialSticks));

    const triggerInitialChange = () => {
      const quantities = Object.entries(initialSticks)
        .filter(([, qty]) => qty > 0)
        .map(([selection_id, qty]) => ({ selection_id, qty }));
      const value = quantities.map(({ selection_id }) => selection_id);

      onChange({ quantities, value });
    };

    triggerInitialChange();
  }, []);

  const onChangeStick = (id: string) => (quantity: number) => {
    if (quantity >= 0) {
      const newSticks: Record<string, number> = {
        ...currentSticks,
        [id]: quantity,
      };
      const currentCount = getCount(newSticks);

      if (currentCount <= sticksCount) {
        setCurrentSticks(newSticks);
        setCount(currentCount);
      }
    }
  };

  useEffect(() => {
    if (currentSticks) {
      const quantities = Object.keys(currentSticks)
        .filter((key) => currentSticks[key] > 0)
        .map((id) => ({
          selection_id: id,
          qty: currentSticks[id],
        }));

      onChange({
        quantities,
        value: quantities.map((quantity) => quantity.selection_id),
      });
    }
  }, [currentSticks]);

  return (
    <Box
      title={`${title} ${count}/${sticksCount}`}
      hasError={showValidation && count < sticksCount}
      data-testid="refill-count"
    >
      {isLoading && <DEPRECATED_Loading />}
      <div className="product-bundles-sticks__listbox" role="list" aria-label={listAriaLabel}>
        {options.map((option: any) => {
          // TODO: add types / DESP-44757 removing any will review type problems
          const inStock = isInStock(stockStatusElements[option.id]);

          return (
            <ProductBundlesSingleStick
              img={option?.product?.thumbnail?.url}
              key={option?.label}
              count={currentSticks[option.id] || 0}
              name={option?.label}
              onChange={onChangeStick(option.id.toString())}
              isInStock={inStock}
              isLoading={isLoading}
              tooltip={option?.product?.tooltipInfo}
              isLimitReached={count >= sticksCount}
            />
          );
        })}
      </div>
    </Box>
  );
};

export default ProductBundlesStickBlock;
